// G:\qkard\frontend\src\pages\WishlistsTab.jsx
// Phase 1F v2 — Zero copy-paste wishlist flow
//
// - Auto-expands newest wishlist
// - Live refresh every 30s with new badge count
// - Send Quote + Create WO in one tap
// - Reference code is fallback only

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { NewOrderDrawer } from "./TailorWorkOrdersTab";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";
const inr = (n) => "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function isNew(iso) {
  return (Date.now() - new Date(iso)) < 5 * 60 * 1000; // within 5 minutes
}

const emptyWOForm = (wishlist) => ({
  customerName:  "",
  customerPhone: "",
  advanceAmount: "",
  deliveryDate:  "",
  notes:         "",
  items: (wishlist?.items || []).map(i => ({
    name:  i.variantSelected ? `${i.name} (${i.variantSelected})` : i.name,
    price: i.price,
  })),
  totalValue: (wishlist?.items || []).reduce((t, i) => t + (i.price || 0), 0),
});

export default function WishlistsTab({ business, onNewCount, customers: customersProp, catalogueItems: catalogueItemsProp, catalogue }) {
  const [wishlists, setWishlists]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [expanded, setExpanded]         = useState(null);
  const [woForm, setWoForm]             = useState(null);
  const [woData, setWoData]             = useState(emptyWOForm(null));
  const [creating, setCreating]         = useState(false);
  const [createdWO, setCreatedWO]       = useState(null);
  const [newCount, setNewCount]         = useState(0);
  const [searchCode, setSearchCode]     = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching]       = useState(false);
  const [searchError, setSearchError]   = useState("");
  const [showSearch, setShowSearch]     = useState(false);
  // Tailor conversion state
  const [tailorDrawer, setTailorDrawer] = useState(null); // { initialDesigns, initialCustomer }
  const [customers, setCustomers]       = useState(customersProp || []);
  const [catalogueItems, setCatalogueItems] = useState(catalogueItemsProp || []);
  const lastSeenRef = useRef(null);
  const intervalRef = useRef(null);

  const isTailor = !!(
    business?.category === "tailor" ||
    business?.category === "boutique" ||
    business?.features?.hasLookbook ||
    catalogue?.features?.hasLookbook
  );

  const load = useCallback(async (silent = false) => {
    if (!business?.businessId) return;
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/wishlists?businessId=${business.businessId}`);
      const data = await res.json();
      const wls = data.wishlists || [];
      setWishlists(wls);

      // Auto-expand newest
      if (wls.length > 0 && !expanded) {
        setExpanded(wls[0].wishlistId);
      }

      // Count new since last seen
      if (lastSeenRef.current) {
        const newOnes = wls.filter(w => new Date(w.createdAt) > lastSeenRef.current).length;
        setNewCount(newOnes);
        if (onNewCount) onNewCount(newOnes);
      } else {
        // First load — mark all as seen
        lastSeenRef.current = new Date();
      }
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [business, expanded, onNewCount]);

  // Initial load
  useEffect(() => {
    load();
  }, [business]);

  // Self-fetch customers and catalogue items if not provided by parent
  useEffect(() => {
    if (!business?.businessId) return;
    if (customers.length === 0) {
      fetch(`${API}/customers?businessId=${business.businessId}`)
        .then(r => r.json())
        .then(d => { if (d.customers?.length) setCustomers(d.customers); })
        .catch(() => {});
    }
    if (catalogueItems.length === 0) {
      // Get catalogue first, then items
      fetch(`${API}/shops?phone=${encodeURIComponent(business.phone || "")}`)
        .then(r => r.json())
        .then(d => {
          const cat = d.catalogues?.[0];
          if (cat?.catalogueId) {
            return fetch(`${API}/items?catalogueId=${cat.catalogueId}`);
          }
        })
        .then(r => r?.json())
        .then(d => { if (d?.items?.length) setCatalogueItems(d.items); })
        .catch(() => {});
    }
  }, [business?.businessId]);

  // Mark as seen when tab opened — reset badge
  useEffect(() => {
    lastSeenRef.current = new Date();
    setNewCount(0);
    if (onNewCount) onNewCount(0);
  }, []);

  // Poll every 30s
  useEffect(() => {
    intervalRef.current = setInterval(() => load(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const handleSearch = async () => {
    const code = searchCode.trim().toUpperCase();
    if (!code) return;
    setSearching(true);
    setSearchResult(null);
    setSearchError("");
    try {
      const res = await fetch(`${API}/wishlists/${code}`);
      const data = await res.json();
      if (data.error) setSearchError(`No wishlist found for "${code}"`);
      else {
        setSearchResult(data.wishlist);
        setExpanded(data.wishlist.wishlistId);
        // Add to top of list if not already there
        setWishlists(prev => {
          if (prev.find(w => w.wishlistId === data.wishlist.wishlistId)) return prev;
          return [data.wishlist, ...prev];
        });
        setShowSearch(false);
        setSearchCode("");
      }
    } catch { setSearchError("Search failed. Try again."); }
    finally { setSearching(false); }
  };

  const openWOForm = (wishlist) => {
    setWoForm(wishlist);
    setWoData(emptyWOForm(wishlist));
    setCreatedWO(null);
  };

  const handleCreateWO = async () => {
    if (!woData.customerName.trim()) { alert("Customer name required"); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API}/workorders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId:    business.businessId,
          customerName:  woData.customerName.trim(),
          customerPhone: woData.customerPhone.trim() || null,
          items:         woData.items.map(i => ({ name: i.name, price: Number(i.price) })),
          advanceAmount: Number(woData.advanceAmount) || 0,
          deliveryDate:  woData.deliveryDate || null,
          notes:         woData.notes.trim(),
        }),
      });
      const data = await res.json();
      if (data.workOrder) setCreatedWO(data.workOrder);
    } catch (e) { alert("Failed: " + e.message); }
    finally { setCreating(false); }
  };

  const sendQuote = (wishlist) => {
    const lines = (wishlist.items || []).map(i =>
      `• ${i.name}${i.variantSelected ? ` (${i.variantSelected})` : ""} — ${inr(i.price)}`
    ).join("\n");
    const text = `Hi! Here is your quote from *${business.businessName}*:\n\n${lines}\n\n*Total: ${inr(wishlist.totalValue || 0)}*\n\nTo confirm, please reply with:\n1. Your name\n2. Preferred delivery date\n\nWe'll arrange the rest! 🙏`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  // Convert wishlist → tailor work order via NewOrderDrawer
  const openTailorConversion = (wishlist) => {
    // Map wishlist items to design objects (tailor schema, no price)
    const initialDesigns = (wishlist.items || []).map(item => ({
      itemId:          item.itemId || "",
      name:            item.name || "",
      category:        item.category || "",
      variantSelected: item.variantSelected || "",
      notes:           "",
      measurements_snapshot: {},
      measurementUnit: "in",
    }));

    // Auto-match customer by phone
    const phone = wishlist.customerPhone || "";
    const matchedCustomer = phone
      ? customers.find(c => (c.phone || "").replace(/\D/g, "") === phone.replace(/\D/g, ""))
      : null;

    setTailorDrawer({ initialDesigns, initialCustomer: matchedCustomer || null });
  };

  return (
    <div style={s.wrap}>

      {/* Header */}
      <div style={s.headerRow}>
        <div style={s.title}>
          Customer Wishlists
          {wishlists.length > 0 && <span style={s.countBadge}>{wishlists.length}</span>}
        </div>
        <button style={s.codeBtn} onClick={() => setShowSearch(v => !v)}>
          🔍 Find by Code
        </button>
      </div>

      {/* Search by code — collapsed by default */}
      {showSearch && (
        <div style={s.searchBox}>
          <div style={s.searchRow}>
            <input style={s.searchInput} placeholder="WISH-XXXX"
              value={searchCode}
              onChange={e => setSearchCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              maxLength={9} autoFocus />
            <button style={s.searchBtn} onClick={handleSearch} disabled={searching}>
              {searching ? "..." : "Find"}
            </button>
          </div>
          {searchError && <div style={s.searchError}>{searchError}</div>}
        </div>
      )}

      {/* Wishlists list */}
      <div style={s.list}>
        {loading && <div style={s.empty}>Loading...</div>}

        {!loading && wishlists.length === 0 && (
          <div style={s.emptyState}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>❤</div>
            <div style={s.emptyTitle}>No wishlists yet</div>
            <div style={s.emptyDesc}>
              When customers browse{" "}
              <b>qkard.app/look/{business?.slug}</b>{" "}
              and save items, they appear here instantly.
            </div>
          </div>
        )}

        {wishlists.map((wishlist, idx) => {
          const isExpanded = expanded === wishlist.wishlistId;
          const fresh = isNew(wishlist.createdAt);

          return (
            <div key={wishlist.wishlistId}
              style={{ ...s.card, ...(fresh ? s.cardNew : {}) }}>

              {/* Card tap to expand */}
              <div style={s.cardHeader}
                onClick={() => setExpanded(isExpanded ? null : wishlist.wishlistId)}>
                <div style={s.cardLeft}>
                  <div style={s.cardTop}>
                    {fresh && <span style={s.newBadge}>NEW</span>}
                    {idx === 0 && !fresh && <span style={s.latestBadge}>LATEST</span>}
                    <span style={s.timeAgo}>{timeAgo(wishlist.updatedAt)}</span>
                  </div>
                  <div style={s.cardSummary}>
                    {wishlist.itemCount} item{wishlist.itemCount !== 1 ? "s" : ""}
                    {" · "}
                    <span style={s.cardTotal}>{inr(wishlist.totalValue || 0)}</span>
                  </div>
                  <div style={s.cardItems}>
                    {(wishlist.items || []).slice(0, 2).map((item, i) => (
                      <span key={i} style={s.cardItemChip}>{item.name}</span>
                    ))}
                    {wishlist.items?.length > 2 && (
                      <span style={s.cardItemChip}>+{wishlist.items.length - 2} more</span>
                    )}
                  </div>
                </div>
                <div style={s.cardRight}>
                  <div style={s.expandIcon}>{isExpanded ? "▲" : "▼"}</div>
                </div>
              </div>

              {/* Expanded */}
              {isExpanded && (
                <div style={s.detail}>
                  {/* Item photos + names */}
                  <div style={s.itemsGrid}>
                    {(wishlist.items || []).map((item, i) => (
                      <div key={i} style={s.itemCard}>
                        {item.photo
                          ? <img src={item.photo} alt={item.name} style={s.itemPhoto} />
                          : <div style={s.itemNoPhoto}>🛋</div>
                        }
                        <div style={s.itemName}>{item.name}</div>
                        {item.variantSelected && (
                          <div style={s.itemVariant}>{item.variantSelected}</div>
                        )}
                        <div style={s.itemPrice}>{inr(item.price)}</div>
                      </div>
                    ))}
                  </div>

                  <div style={s.totalRow}>
                    <span>Total</span>
                    <span style={{ fontWeight: 800, color: "#b8722a" }}>{inr(wishlist.totalValue || 0)}</span>
                  </div>

                  {/* Ref code — small, secondary */}
                  <div style={s.refCodeSmall}>Ref: {wishlist.referenceCode}</div>

                  {/* Primary actions */}
                  <div style={s.actions}>
                    <button style={s.quoteBtn} onClick={() => sendQuote(wishlist)}>
                      💬 Send Quote
                    </button>
                    <button
                      style={s.createWOBtn}
                      onClick={() => isTailor ? openTailorConversion(wishlist) : openWOForm(wishlist)}
                    >
                      📋 Create Work Order
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Create Work Order Drawer ──────────────────────────────────── */}
      {woForm && (
        <div style={s.overlay} onClick={() => !creating && setWoForm(null)}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>
            <div style={s.drawerHeader}>
              <div>
                <div style={s.drawerTitle}>Create Work Order</div>
                <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                  {woForm.itemCount} items · {inr(woForm.totalValue || 0)}
                </div>
              </div>
              <button style={s.closeBtn} onClick={() => !creating && setWoForm(null)}>✕</button>
            </div>

            <div style={s.drawerBody}>
              {createdWO ? (
                <div style={s.successBox}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                  <div style={s.successTitle}>Work Order Created!</div>
                  <div style={s.successRef}>{createdWO.referenceCode}</div>
                  <div style={s.successMeta}>
                    {createdWO.customerName} · {inr(createdWO.totalValue)}
                    {createdWO.balanceAmount > 0 && ` · Bal: ${inr(createdWO.balanceAmount)}`}
                  </div>
                  {createdWO.customerPhone && (
                    <button style={s.whatsappBtnFull} onClick={() => {
                      const items = (createdWO.items || []).map(i => `• ${i.name} — ${inr(i.price)}`).join("\n");
                      const text = `Hi ${createdWO.customerName}! Your order *${createdWO.referenceCode}* is confirmed ✅\n\n${items}\n\nTotal: ${inr(createdWO.totalValue)}\nAdvance paid: ${inr(createdWO.advanceAmount)}\nBalance due: ${inr(createdWO.balanceAmount)}${createdWO.deliveryDate ? `\nDelivery by: ${createdWO.deliveryDate}` : ""}\n\nThank you! 🙏`;
                      window.open(`https://wa.me/${createdWO.customerPhone.replace(/\D/g,"")}?text=${encodeURIComponent(text)}`, "_blank");
                    }}>
                      📲 Send Confirmation on WhatsApp
                    </button>
                  )}
                  <button style={s.doneBtn} onClick={() => setWoForm(null)}>Done</button>
                </div>
              ) : (
                <>
                  {/* Pre-filled items — read only */}
                  <div style={s.section}>
                    <div style={s.label}>Items</div>
                    <div style={s.prefilledItems}>
                      {woData.items.map((item, i) => (
                        <div key={i} style={s.prefilledItem}>
                          <span style={{ flex: 1, fontSize: 13, color: "#333" }}>{item.name}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#b8722a" }}>{inr(item.price)}</span>
                        </div>
                      ))}
                      <div style={s.prefilledTotal}>
                        <span>Total</span><span>{inr(woData.totalValue)}</span>
                      </div>
                    </div>
                  </div>

                  <div style={s.section}>
                    <div style={s.label}>Customer Name *</div>
                    <input style={s.input} placeholder="e.g. Ramesh Gupta"
                      value={woData.customerName}
                      onChange={e => setWoData(f => ({ ...f, customerName: e.target.value }))} />
                  </div>

                  <div style={s.section}>
                    <div style={s.label}>Customer Phone</div>
                    <input style={s.input} placeholder="+91XXXXXXXXXX" type="tel"
                      value={woData.customerPhone}
                      onChange={e => setWoData(f => ({ ...f, customerPhone: e.target.value }))} />
                  </div>

                  <div style={s.section}>
                    <div style={s.label}>Advance Received (₹)</div>
                    <input style={s.input} type="number" placeholder="0"
                      value={woData.advanceAmount}
                      onChange={e => setWoData(f => ({ ...f, advanceAmount: e.target.value }))} />
                    {Number(woData.advanceAmount) > 0 && (
                      <div style={{ fontSize: 12, color: "#e03535", marginTop: 4 }}>
                        Balance due: {inr(Math.max(0, woData.totalValue - Number(woData.advanceAmount)))}
                      </div>
                    )}
                  </div>

                  <div style={s.section}>
                    <div style={s.label}>Delivery Date</div>
                    <input style={s.input} type="date"
                      value={woData.deliveryDate}
                      onChange={e => setWoData(f => ({ ...f, deliveryDate: e.target.value }))} />
                  </div>

                  <div style={s.section}>
                    <div style={s.label}>Notes</div>
                    <textarea style={{ ...s.input, minHeight: 64, resize: "vertical" }}
                      placeholder="Measurements, colour preference, special instructions..."
                      value={woData.notes}
                      onChange={e => setWoData(f => ({ ...f, notes: e.target.value }))} />
                  </div>

                  <button style={{ ...s.saveBtn, ...(creating ? { background: "#999" } : {}) }}
                    onClick={handleCreateWO} disabled={creating}>
                    {creating ? "Creating..." : "Create Work Order"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Tailor: Convert Wishlist → Work Order via NewOrderDrawer ── */}
      {tailorDrawer && (
        <NewOrderDrawer
          business={business}
          catalogueItems={catalogueItems}
          customers={customers}
          initialDesigns={tailorDrawer.initialDesigns}
          initialCustomer={tailorDrawer.initialCustomer}
          onClose={() => setTailorDrawer(null)}
          onCreate={(newOrder) => {
            setTailorDrawer(null);
            // Optionally show a success toast — for now just close
          }}
        />
      )}
    </div>
  );
}

const s = {
  wrap: { padding: "0 0 80px" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 16px 12px" },
  title: { fontSize: 18, fontWeight: 700, color: "#111", display: "flex", alignItems: "center", gap: 8 },
  countBadge: { background: "#111", color: "#fff", fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 10 },
  codeBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#666" },
  searchBox: { margin: "0 16px 12px", background: "#fff", borderRadius: 12, padding: "12px 14px", border: "1px solid #f0f0f0" },
  searchRow: { display: "flex", gap: 8 },
  searchInput: { flex: 1, padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 15, fontWeight: 600, letterSpacing: "1px", color: "#111", fontFamily: "monospace", outline: "none", background: "#fafafa" },
  searchBtn: { padding: "10px 16px", borderRadius: 8, border: "none", background: "#111", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  searchError: { marginTop: 8, fontSize: 13, color: "#e03535" },
  list: { padding: "0 16px" },
  emptyState: { textAlign: "center", padding: "48px 24px" },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 8 },
  emptyDesc: { fontSize: 14, color: "#999", lineHeight: 1.6 },
  empty: { textAlign: "center", padding: "32px", color: "#999" },
  card: { background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: 10, overflow: "hidden", transition: "box-shadow 0.2s" },
  cardNew: { border: "2px solid #22c55e", boxShadow: "0 4px 16px rgba(34,197,94,0.15)" },
  cardHeader: { padding: "14px 16px", cursor: "pointer" },
  cardLeft: { flex: 1 },
  cardTop: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  newBadge: { background: "#22c55e", color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 10, letterSpacing: "0.5px" },
  latestBadge: { background: "#f0f0f0", color: "#888", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 },
  timeAgo: { fontSize: 12, color: "#bbb" },
  cardSummary: { fontSize: 16, fontWeight: 600, color: "#333", marginBottom: 6 },
  cardTotal: { color: "#b8722a", fontWeight: 700 },
  cardItems: { display: "flex", gap: 6, flexWrap: "wrap" },
  cardItemChip: { fontSize: 11, color: "#666", background: "#f5f5f5", padding: "2px 8px", borderRadius: 6 },
  cardRight: { position: "absolute", right: 16, top: 16 },
  expandIcon: { fontSize: 10, color: "#ccc" },
  detail: { borderTop: "1px solid #f5f5f5", padding: "12px 16px 16px" },
  itemsGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 },
  itemCard: { background: "#faf8f5", borderRadius: 8, overflow: "hidden", textAlign: "center" },
  itemPhoto: { width: "100%", aspectRatio: "1/1", objectFit: "cover" },
  itemNoPhoto: { width: "100%", aspectRatio: "1/1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, background: "#f0ebe4" },
  itemName: { fontSize: 10, fontWeight: 600, color: "#333", padding: "4px 6px 0", lineHeight: 1.3 },
  itemVariant: { fontSize: 9, color: "#999", padding: "0 6px" },
  itemPrice: { fontSize: 11, fontWeight: 700, color: "#b8722a", padding: "2px 6px 6px" },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: 15, color: "#111", padding: "8px 0", borderTop: "1px solid #f0f0f0", marginBottom: 4 },
  refCodeSmall: { fontSize: 11, color: "#ccc", marginBottom: 12, fontFamily: "monospace" },
  actions: { display: "flex", gap: 8 },
  quoteBtn: { flex: 1, padding: "11px", borderRadius: 8, border: "1.5px solid #25D366", background: "#fff", color: "#25D366", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  createWOBtn: { flex: 1, padding: "11px", borderRadius: 8, border: "none", background: "#111", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 500, display: "flex", alignItems: "flex-end" },
  drawer: { background: "#fff", width: "100%", maxWidth: 640, margin: "0 auto", borderRadius: "20px 20px 0 0", maxHeight: "92vh", display: "flex", flexDirection: "column" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "16px 20px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 },
  drawerTitle: { fontSize: 17, fontWeight: 700, color: "#111" },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#999" },
  drawerBody: { overflowY: "auto", padding: "16px 20px 32px", flex: 1 },
  section: { marginBottom: 20 },
  label: { fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 8 },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 14, color: "#111", fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "#fafafa" },
  prefilledItems: { background: "#faf8f5", borderRadius: 10, overflow: "hidden", border: "1px solid #ede8e1" },
  prefilledItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #ede8e1" },
  prefilledTotal: { display: "flex", justifyContent: "space-between", padding: "10px 14px", fontSize: 14, fontWeight: 700, color: "#111" },
  saveBtn: { width: "100%", padding: 14, borderRadius: 10, border: "none", background: "#111", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" },
  successBox: { textAlign: "center", padding: "20px 0" },
  successTitle: { fontSize: 20, fontWeight: 700, color: "#111", marginBottom: 8 },
  successRef: { fontSize: 24, fontWeight: 900, fontFamily: "monospace", color: "#111", letterSpacing: "2px", marginBottom: 8 },
  successMeta: { fontSize: 14, color: "#888", marginBottom: 24 },
  whatsappBtnFull: { width: "100%", padding: 14, borderRadius: 10, border: "none", background: "#25D366", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 12 },
  doneBtn: { width: "100%", padding: 13, borderRadius: 10, border: "1.5px solid #ddd", background: "transparent", color: "#666", fontSize: 14, fontWeight: 600, cursor: "pointer" },
};
